import React, { useRef, useState, useEffect } from "react";
import {
  Collapse,
  Form,
  message,
  Typography,
  Button,
  Row,
  Col,
} from "antd";
import { CloseOutlined } from "@ant-design/icons";
import ConsultantForm from "./CommonComponents/ConsultantForm";
import PatientProfile from "./CommonComponents/PatientProfile";
import ClinicalImpression from "./CommonComponents/ClinicalImpression";
import HistoryIllness from "./CommonComponents/HistoryIllness";
import FunctionalStatus from "./CommonComponents/FunctionalStatus";
import ClinicalProgressDeath from "./CommonComponents/ClinicalProgress/ClinicalProgressDeath";
import CardioRespiratoryArrest from "./CommonComponents/CardioRespiratoryArrest";
import btnStyles from "../../../styles/Custom/Button.module.css";
import {
  getDeathFormApi,
  getPatientDetailsApi,
  printFormApi,
  submitDeathFormApi,
  shareIpFormApi,
  updateDeathFormApi,
} from "../../../services/forms.services";
import {
  getLabelTagsWithFormDescription,
  getPastMedicalHistoryFormSummary,
  printFormSummary,
} from "./formsKeys";
import {
  knownAllergiesOptions,
  pastMedicalOptions,
} from "../../IpEMR/Options/historyOptions";
import dayjs from "dayjs";
import useQuery from "../../../hooks/useQuery";
import NoDataPage from "../../NoDataPages/NoDataPage";
import assessmentIcon from "../../../assets/images/nurseIcons/assessment.svg";
import FormContentLoader from "./FormContentLoader";
import ScaledHtmlPreview from "../../ui/ScaledHtmlPreview";
import ShareModal from "../Forms/DischargeSummary/ShareModal";
import "./CommonComponents/FormDocument.css";
import "./FormsIp.css";

const DeathSummary = ({ patientData, role }) => {
  const [form] = Form.useForm();
  const formRef = useRef(null);
  const [pressureSorePoints, setPressureSorePoints] = useState([]);
  const [deathFormData, setDeathFormData] = useState(null);
  const [editable, setEditable] = useState(false);
  const [openShare, setOpenShare] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [details, setDetails] = useState(null);
  const [displayForm, setDisplayForm] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const refreshPage = () => setRefresh((prev) => !prev);
  const isActive = useQuery()?.status === "current";
  const isReceptionist = role === "receptionist";
  const canEditForm = isActive && !isReceptionist && editable;

  useEffect(() => {
    if (!patientData?.admission?.id || !patientData?.doctor_intial_assessment) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setDisplayForm(null);

    const loadForm = async () => {
      try {
        const [deathRes, detailsRes] = await Promise.all([
          getDeathFormApi(patientData.admission.id).catch((err) => {
            if (err?.response?.status === 404) return null;
            throw err;
          }),
          getPatientDetailsApi(patientData.admission.id),
        ]);
        if (cancelled) return;

        const deathData = deathRes?.data ?? null;
        setDeathFormData(deathData);
        setDetails(detailsRes?.data);

        const formId = deathData?.data?.id;
        if (isReceptionist || !isActive) {
          setEditable(false);
        } else {
          setEditable(formId ? false : true);
        }

        if (formId) {
          const previewRes = await printFormApi(
            `form_type=death&form_id=${formId}`
          );
          if (!cancelled) {
            setDisplayForm(previewRes?.data?.data ?? null);
          }
        } else if (!cancelled) {
          setDisplayForm(null);
        }
      } catch (err) {
        console.log(err);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadForm();
    return () => {
      cancelled = true;
    };
  }, [patientData?.admission?.id, patientData?.doctor_intial_assessment, refresh, isReceptionist, isActive]);

  useEffect(() => {
    const clinical = deathFormData?.data?.data?.clinical_progress;
    const deathDate = clinical?.death_date
      ? dayjs(clinical.death_date)
      : null;
    form.setFieldsValue({
      ...deathFormData?.data?.data,
      clinical_progress: {
        ...clinical,
        date: clinical?.date ? dayjs(clinical.date) : null,
        death_date: deathDate,
        death_date_part: deathDate,
        death_time_part: deathDate,
      },
    });
    if (deathFormData?.data?.data?.pressure_sore) {
      setPressureSorePoints(deathFormData?.data?.data?.pressure_sore);
    }
  }, [deathFormData?.data?.data]);

  const onFinish = (values) => {
    const clinical = values?.clinical_progress || {};
    let deathDate = clinical.death_date;
    if (clinical.death_date_part) {
      deathDate = dayjs(clinical.death_date_part);
      if (clinical.death_time_part) {
        deathDate = deathDate
          .hour(dayjs(clinical.death_time_part).hour())
          .minute(dayjs(clinical.death_time_part).minute())
          .second(0);
      }
    }
    const {
      death_date_part,
      death_time_part,
      ...clinicalRest
    } = clinical;

    const formData = {
      patient: details?.data?.patient,
      diagnosis: details?.data?.diagnosis,
      medications: details?.data?.medications,
      functional_status: details?.data?.functional_status,
      vitals: details?.data?.vitals,
      nebulization: details?.data?.nebulization,
      past_medical_history: details?.data?.present_illness
        ? getPastMedicalHistoryFormSummary(
            pastMedicalOptions,
            details?.data?.present_illness
          )
        : "",
      known_allergies: details?.data?.present_illness
        ? getLabelTagsWithFormDescription(
            knownAllergiesOptions,
            details?.data?.present_illness
          )
        : "",
      past_surgical_history: details?.data?.present_illness?.surgical_history
        ? details?.data?.present_illness?.surgical_history
        : null,
      past_surgical_history_details: details?.data?.present_illness
        ?.surgical_history_details
        ? details?.data?.present_illness?.surgical_history_details
        : null,
      ...values,

      clinical_progress: {
        ...clinicalRest,
        date: clinicalRest.date
          ? dayjs(clinicalRest.date).format("YYYY-MM-DD")
          : null,
        death_date: deathDate
          ? dayjs(deathDate).format("YYYY-MM-DD hh:mm A")
          : null,
      },
      pressure_sore: pressureSorePoints,
    };

    if (deathFormData?.data?.id) {
      updateDeathFormApi(patientData?.admission?.id, { data: formData })
        .then((res) => {
          message.success(res?.data?.message);
          setEditable(false);
          refreshPage();
        })
        .catch((err) => message.error(err?.response?.data?.message));
    } else {
      submitDeathFormApi({
        ip_admission: patientData?.admission?.id,
        data: formData,
      })
        .then((res) => {
          message.success(res?.data?.message);
          setEditable(false);
          refreshPage();
        })
        .catch((err) => message.error(err?.response?.data?.message));
    }
  };
  const commonStyle = {
    // background: "#fff",
    boxShadow:
      "0px 2px 4px -2px rgba(16, 24, 40, 0.06), 0px 4px 8px -2px rgba(16, 24, 40, 0.1)",
  };

  // console.log(pressureSorePoints, "pressureSorePoints");
  return (
    <>
      {!patientData?.doctor_intial_assessment ? (
        <Row
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "80vh",
          }}
        >
          <Col span={24} style={{ textAlign: "center" }}>
            <NoDataPage
              title={"Initial Assessment Not Yet Submitted "}
              subtitle={"Kindly, submit initial assessment first"}
              image={assessmentIcon}
            />
          </Col>
        </Row>
      ) : (
        <FormContentLoader loading={isLoading} tip="Loading death summary...">
        <div style={{ position: "relative" }}>
          <div className="forms-ip-header">
            <Typography.Title level={5} className="forms-ip-header-title">
              Death Summary
            </Typography.Title>
            {deathFormData?.data?.id && (
              <div className="forms-ip-header-actions">
                {isActive && !["receptionist"]?.includes(role) && (
                  <Button
                    className="ds-btn ds-btn--ghost"
                    onClick={() => setEditable(true)}
                  >
                    Edit
                  </Button>
                )}
                <Button
                  className="ds-btn ds-btn--ghost"
                  onClick={() =>
                    printFormSummary("death", deathFormData?.data?.id)
                  }
                >
                  Print
                </Button>
                <Button
                  className="ds-btn ds-btn--primary"
                  onClick={() => setOpenShare(true)}
                >
                  Share
                </Button>
                {canEditForm && (
                  <CloseOutlined onClick={() => setEditable(false)} />
                )}
              </div>
            )}
          </div>
          {canEditForm ? (
            <Form
              form={form}
              ref={formRef}
              layout="vertical"
              onFinish={onFinish}
              className="forms-ip-shell forms-ip-scroll-shell"
            >
              <Collapse
                expandIconPosition="right"
                className="custom-collapse"
                defaultActiveKey={["1", "2", "3", "4", "5", "6", "7", "8", "9"]}
              >
                <Collapse.Panel
                  header="Patient Profile"
                  style={commonStyle}
                  key="1"
                >
                  <PatientProfile data={patientData?.admission} />
                </Collapse.Panel>
                <Collapse.Panel
                  header="Consultants"
                  style={commonStyle}
                  key="2"
                >
                  <ConsultantForm form={form} />
                </Collapse.Panel>
                <Collapse.Panel
                  header="Clinical Impression/ Diagnosis"
                  style={commonStyle}
                  key="3"
                >
                  <ClinicalImpression
                    form={form}
                    details={details?.data?.diagnosis}
                  />
                </Collapse.Panel>
                <Collapse.Panel
                  header="History of Present Illness"
                  style={commonStyle}
                  key="4"
                >
                  <HistoryIllness
                    form={form}
                    patientData={patientData?.admission}
                    data={details?.data?.present_illness}
                  />
                </Collapse.Panel>
                <Collapse.Panel
                  header="Functional Status at the time of Admission"
                  style={commonStyle}
                  key="5"
                >
                  <FunctionalStatus
                    form={form}
                    details={details?.data?.functional_status}
                  />
                </Collapse.Panel>
                <Collapse.Panel
                  header="Clinical Progress"
                  style={commonStyle}
                  key="6"
                >
                  <ClinicalProgressDeath
                    form={form}
                    setPressureSorePoints={setPressureSorePoints}
                    pressureSorePoints={pressureSorePoints}
                  />
                </Collapse.Panel>
              </Collapse>
              <div style={{ marginBottom: "40px" }}>
                <CardioRespiratoryArrest />
              </div>

              <div
                className="ip-fixed-form-footer"
                style={{
                  position: "fixed",
                  bottom: 0,
                  left: 0,

                  width: "100%",
                  background: "#fff",
                  paddingTop: "10px",
                  paddingRight: "16px",
                  boxShadow: "0px -2px 4px rgba(0, 0, 0, 0.1)",
                  textAlign: "right",
                  zIndex: 1000, // Ensure it stays above other elements
                }}
              >
                {isActive && !["receptionist"]?.includes(role) && (
                  <Form.Item>
                    <Button
                      htmlType="submit"
                      type="primary"
                      className={btnStyles.lgBtn}
                    >
                      Submit
                    </Button>
                  </Form.Item>
                )}
              </div>
            </Form>
          ) : isReceptionist && !deathFormData?.data?.id ? (
            <NoDataPage
              title="Death Summary Form Not Available"
              subtitle="Death summary has not been submitted yet"
              image={assessmentIcon}
            />
          ) : displayForm ? (
            <ScaledHtmlPreview
              html={displayForm}
              designWidth={900}
              className="ip-form-html-preview"
            />
          ) : (
            <NoDataPage
              title="Death Summary Form Not Available"
              subtitle="Death summary form is not available for discharged patients"
              image={assessmentIcon}
            />
          )}
        </div>
        </FormContentLoader>
      )}
      {openShare && (
        <ShareModal
          openModal={openShare}
          setOpenModal={setOpenShare}
          patientData={patientData}
          title="Share Death Summary"
          documentLabel="Death summary"
          shareApi={(payload) =>
            shareIpFormApi("death", deathFormData?.data?.id, payload)
          }
        />
      )}
    </>
  );
};

export default DeathSummary;
